/**
 * Profile Service - Handles user profile operations with Supabase
 * Includes snake_case ↔ camelCase conversion
 */

import { supabase } from '../lib/supabase';

// Convert database snake_case to React camelCase
const convertProfileFromDb = (profile) => {
  if (!profile) return null;
  
  return {
    id: profile?.id,
    email: profile?.email,
    fullName: profile?.full_name,
    firstName: profile?.first_name,
    lastName: profile?.last_name,
    companyName: profile?.company_name,
    phone: profile?.phone,
    mobile: profile?.mobile,
    businessAddress: profile?.business_address,
    businessType: profile?.business_type,
    industryType: profile?.industry_type,
    jobTitle: profile?.job_title,
    taxId: profile?.tax_id,
    website: profile?.website,
    avatarUrl: profile?.avatar_url,
    role: profile?.role,
    partnerStatus: profile?.partner_status,
    salesforceAccountId: profile?.salesforce_account_id,
    salesforceContactId: profile?.salesforce_contact_id,
    cesspoolConversionRewardAmount: profile?.cesspool_conversion_reward_amount,
    otherReferralRewardAmount: profile?.other_referral_reward_amount,
    createdAt: profile?.created_at,
    updatedAt: profile?.updated_at
  };
};

// Convert React camelCase to database snake_case
const convertProfileToDb = (profile) => {
  return {
    full_name: profile?.fullName,
    first_name: profile?.firstName,
    last_name: profile?.lastName,
    company_name: profile?.companyName,
    phone: profile?.phone,
    mobile: profile?.mobile,
    business_address: profile?.businessAddress,
    business_type: profile?.businessType,
    industry_type: profile?.industryType,
    job_title: profile?.jobTitle,
    tax_id: profile?.taxId,
    website: profile?.website,
    avatar_url: profile?.avatarUrl,
    partner_status: profile?.partnerStatus,
    salesforce_account_id: profile?.salesforceAccountId,
    salesforce_contact_id: profile?.salesforceContactId,
    cesspool_conversion_reward_amount: profile?.cesspoolConversionRewardAmount,
    other_referral_reward_amount: profile?.otherReferralRewardAmount
  };
};

export const profileService = {
  /**
   * Fetches the current user's profile from Supabase
   * @returns {Promise<Object>} Object containing profile data or error
   */
  async getUserProfile() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        return { data: null, error: 'Not authenticated' };
      }

      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('*')
        ?.eq('id', user?.id)
        ?.single();

      if (error) {
        console.error('Error fetching profile:', error);
        return { data: null, error: error?.message };
      }

      return convertProfileFromDb(data);
    } catch (error) {
      console.error('Error in getUserProfile:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Updates the user's profile in Supabase and syncs to Salesforce
   * @param {Object} updates - Profile updates (camelCase format)
   * @returns {Promise<Object>} Object containing updated profile data or error
   */
  async updateUserProfile(updates) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      if (!user) {
        return { data: null, error: 'Not authenticated' };
      }

      // Convert camelCase to snake_case for database
      const dbUpdates = convertProfileToDb(updates);

      // Update in Supabase
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update(dbUpdates)
        ?.eq('id', user?.id)
        ?.select()
        ?.single();

      if (error) {
        console.error('Error updating profile:', error);
        return { data: null, error: error?.message };
      }

      // Convert back to camelCase for response
      const updatedProfile = convertProfileFromDb(data);

      // Sync to Salesforce if Salesforce IDs exist
      if (data?.salesforce_account_id && data?.salesforce_contact_id) {
        try {
          await syncProfileToSalesforce(updatedProfile);
        } catch (sfError) {
          console.error('Salesforce sync failed (non-fatal):', sfError);
          // Continue even if Salesforce sync fails
        }
      }

      return { data: updatedProfile, error: null };
    } catch (error) {
      console.error('Error in updateUserProfile:', error);
      return { data: null, error: error?.message };
    }
  },

  /**
   * Syncs profile updates to Salesforce
   * @param {Object} profile - Profile data to sync
   * @returns {Promise<void>}
   */
  async syncProfileToSalesforce(profile) {
    try {
      const partnerData = {
        companyName: profile?.companyName,
        firstName: profile?.firstName,
        lastName: profile?.lastName,
        email: profile?.email,
        phone: profile?.phone,
        mobile: profile?.mobile,
        taxId: profile?.taxId,
        industryType: profile?.industryType,
        businessType: profile?.businessType,
        website: profile?.website,
        businessAddress: profile?.businessAddress,
        jobTitle: profile?.jobTitle,
        accountId: profile?.salesforceAccountId,
        contactId: profile?.salesforceContactId,
      };

      const { data, error } = await supabase?.functions?.invoke('salesforce-partner', {
        body: { 
          partnerData,
          isUpdate: true
        }
      });

      if (error) {
        throw new Error(`Salesforce sync failed: ${error?.message}`);
      }

      console.log('✅ Profile synced to Salesforce successfully:', data);
    } catch (error) {
      console.error('❌ Salesforce sync error:', error);
      throw error;
    }
  },

  /**
   * Update partner reward amounts (admin only)
   */
  async updatePartnerRewardAmounts(partnerId, rewardAmounts) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.update({
          cesspool_conversion_reward_amount: rewardAmounts?.cesspoolConversionRewardAmount,
          other_referral_reward_amount: rewardAmounts?.otherReferralRewardAmount
        })
        ?.eq('id', partnerId)
        ?.select()
        ?.single();

      if (error) throw error;
      
      return convertProfileFromDb(data);
    } catch (error) {
      console.error('Error updating partner reward amounts:', error);
      throw error;
    }
  },

  /**
   * Get partner reward amounts
   */
  async getPartnerRewardAmounts(partnerId) {
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('cesspool_conversion_reward_amount, other_referral_reward_amount')
        ?.eq('id', partnerId)
        ?.single();

      if (error) throw error;
      
      return {
        cesspoolConversionRewardAmount: data?.cesspool_conversion_reward_amount,
        otherReferralRewardAmount: data?.other_referral_reward_amount
      };
    } catch (error) {
      console.error('Error fetching partner reward amounts:', error);
      throw error;
    }
  }
};

// Export named functions from profileService
export const getUserProfile = profileService?.getUserProfile;
export const updateUserProfile = profileService?.updateUserProfile;
export const updatePartnerRewardAmounts = profileService?.updatePartnerRewardAmounts;
export const getPartnerRewardAmounts = profileService?.getPartnerRewardAmounts;

// Default export
export default profileService;